import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Avatar,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LinkOff as UnlinkIcon,
  PersonSearch as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

interface Review {
  review_id: string;
  rating: number;
  text?: string;
  author_name?: string;
  profile_photo_url?: string;
  relative_time?: string;
  published_at?: string;
  google_maps_uri?: string;
}

interface ReviewLink {
  review_id: string;
  reviewer_name: string | null;
  account_id: string;
  account_number: string;
  account_name: string;
  linked_at: string;
}

interface AccountOption {
  id: string;
  sonic_account_number: string | null;
  display_name: string | null;
  email: string | null;
}

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <Box display="flex" gap={0.25}>
    {[1, 2, 3, 4, 5].map((n) =>
      n <= rating
        ? <StarIcon key={n} sx={{ fontSize: 18, color: '#f59e0b' }} />
        : <StarBorderIcon key={n} sx={{ fontSize: 18, color: '#f59e0b' }} />
    )}
  </Box>
);

const GoogleReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [links, setLinks] = useState<ReviewLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [placeId, setPlaceId] = useState<string | null>(null);

  // Link dialog state
  const [dialogReview, setDialogReview] = useState<Review | null>(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [accountSearchLoading, setAccountSearchLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dbReviews, stats, linkData] = await Promise.all([
        apiService.adminGetPersistedReviews().catch(() => []),
        apiService.adminGetGoogleReviewStats().catch(() => null),
        apiService.adminGetReviewLinks().catch(() => []),
      ]);
      setReviews(dbReviews);
      if (stats?.place_id) setPlaceId(stats.place_id);
      if (!stats?.place_id && dbReviews.length === 0) {
        setError('Place ID and API key are not configured. Go to Settings → Communications to set them up.');
      }
      setLinks(linkData);
    } catch {
      setError('Failed to load reviews. Check that your Place ID and API key are configured in Settings → Communications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Account autocomplete search
  useEffect(() => {
    if (!accountSearch || accountSearch.length < 2) { setAccountOptions([]); return; }
    const timer = setTimeout(async () => {
      setAccountSearchLoading(true);
      try {
        const result = await apiService.getAccounts({ q: accountSearch, per_page: 10 });
        setAccountOptions(result.accounts || []);
      } catch {
        setAccountOptions([]);
      } finally {
        setAccountSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [accountSearch]);

  const getLink = (reviewId: string) => links.find((l) => l.review_id === reviewId) ?? null;

  const handleOpenDialog = (review: Review) => {
    setDialogReview(review);
    setSelectedAccount(null);
    setAccountSearch('');
    setAccountOptions([]);
  };

  const handleLink = async () => {
    if (!dialogReview || !selectedAccount) return;
    setLinking(true);
    try {
      await apiService.adminCreateReviewLink(
        dialogReview.review_id,
        selectedAccount.id,
        dialogReview.author_name,
      );
      await load();
      setDialogReview(null);
    } catch {
      // keep dialog open
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (reviewId: string) => {
    setUnlinking(reviewId);
    try {
      await apiService.adminDeleteReviewLink(reviewId);
      setLinks((prev) => prev.filter((l) => l.review_id !== reviewId));
    } finally {
      setUnlinking(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await apiService.adminTriggerReviewSync();
      await load();
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Google Reviews</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Recent reviews from your Google Business Profile. Link each reviewer to a SonOps account.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon fontSize="small" />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>
          {placeId && (
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              href={`https://www.google.com/maps/place/?q=place_id:${placeId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Google Maps
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

      {!error && reviews.length === 0 && (
        <Alert severity="info">
          No reviews found. Make sure the Place ID is saved in{' '}
          <RouterLink to="/settings" style={{ color: 'inherit' }}>Settings → Communications</RouterLink>.
        </Alert>
      )}

      <Grid container spacing={2}>
        {reviews.map((review: Review) => {
          const link = getLink(review.review_id);
          const authorName = review.author_name || 'Anonymous';
          const photoUrl = review.profile_photo_url;
          const reviewText = review.text;
          const relTime = review.relative_time;

          return (
            <Grid item xs={12} md={6} key={review.review_id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  {/* Header row */}
                  <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                    <Avatar
                      src={photoUrl}
                      alt={authorName}
                      sx={{ width: 40, height: 40, flexShrink: 0 }}
                    >
                      {authorName[0]}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>{authorName}</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Stars rating={review.rating} />
                        {relTime && (
                          <Typography variant="caption" color="text.secondary">{relTime}</Typography>
                        )}
                      </Box>
                    </Box>
                    {review.google_maps_uri && (
                      <Tooltip title="View review on Google">
                        <IconButton size="small" href={review.google_maps_uri} target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Review text */}
                  {reviewText && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                      {reviewText}
                    </Typography>
                  )}

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Account link row */}
                  {link ? (
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">Linked to:</Typography>
                      <Chip
                        label={`${link.account_name}${link.account_number ? ` · #${link.account_number}` : ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        component={RouterLink}
                        to={`/accounts/${link.account_id}`}
                        clickable
                      />
                      <Tooltip title="Remove link">
                        <IconButton
                          size="small"
                          onClick={() => handleUnlink(review.review_id)}
                          disabled={unlinking === review.review_id}
                        >
                          {unlinking === review.review_id ? <CircularProgress size={14} /> : <UnlinkIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        startIcon={<LinkIcon fontSize="small" />}
                        onClick={() => handleOpenDialog(review)}
                        sx={{ ml: 'auto' }}
                      >
                        Change
                      </Button>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        Not linked to a customer
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon fontSize="small" />}
                        onClick={() => handleOpenDialog(review)}
                      >
                        Link to customer
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Link dialog */}
      <Dialog open={Boolean(dialogReview)} onClose={() => setDialogReview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link review to customer</DialogTitle>
        <DialogContent>
          {dialogReview && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Reviewer: <strong>{dialogReview.author_name || 'Anonymous'}</strong>
              </Typography>
            </Box>
          )}
          <Autocomplete
            options={accountOptions}
            getOptionLabel={(o) => `${o.display_name || o.email || ''}${o.sonic_account_number ? ` (#${o.sonic_account_number})` : ''}`}
            loading={accountSearchLoading}
            inputValue={accountSearch}
            value={selectedAccount}
            onInputChange={(_, v) => setAccountSearch(v)}
            onChange={(_, v) => setSelectedAccount(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search accounts"
                placeholder="Type name, email or account number…"
                size="small"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {accountSearchLoading && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogReview(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleLink}
            disabled={!selectedAccount || linking}
            startIcon={linking ? <CircularProgress size={16} /> : undefined}
          >
            {linking ? 'Linking…' : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoogleReviewsPage;
